import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '@/shared/i18n/useT';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';
import { IconButton } from './IconButton';
import { useRestoreFocus } from './useRestoreFocus';

const SIDES = {
  auto: 'anim-sheet anim-sheet-auto inset-x-0 bottom-0 max-h-[90dvh] rounded-t-xl lg:inset-y-0 lg:left-auto lg:right-0 lg:max-h-none lg:w-[28rem] lg:rounded-none lg:rounded-l-xl',
  bottom: 'anim-sheet inset-x-0 bottom-0 max-h-[90dvh] rounded-t-xl',
  right: 'anim-sheet-side inset-y-0 right-0 w-[min(28rem,100%)] rounded-l-xl',
  left: 'anim-sheet-side inset-y-0 left-0 w-[min(28rem,100%)] rounded-r-xl',
} as const;

export interface SheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  /** `auto`: hoja inferior en móvil y panel lateral derecho desde 1024 px. */
  side?: keyof typeof SIDES;
  className?: string;
}

/** Hoja de contenido (Radix Dialog): foco atrapado, Esc cierra, respeta el área segura inferior. */
export function Sheet({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  side = 'auto',
  className,
}: SheetProps) {
  const { t } = useT();
  const restoreFocus = useRestoreFocus(open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="anim-overlay fixed inset-0 z-50 bg-board/60" />
        <DialogPrimitive.Content
          onCloseAutoFocus={restoreFocus}
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'surface-float fixed z-50 flex flex-col overflow-hidden border-b-0',
            SIDES[side],
            className,
          )}
        >
          <div
            aria-hidden
            className={cn(
              'mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border',
              side === 'auto' ? 'lg:hidden' : side !== 'bottom' && 'hidden',
            )}
          />
          <header className="flex items-start justify-between gap-4 px-6 pb-2 pt-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="font-display text-xl font-bold text-fg">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm text-fg-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close asChild>
              <IconButton
                label={t('actions.close')}
                icon={<Icon icon={X} />}
                iconSize="sm"
                className="-mr-2 shrink-0"
              />
            </DialogPrimitive.Close>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-2">{children}</div>
          {footer ? (
            <footer className="flex flex-col-reverse gap-2 border-t border-border-soft px-6 pb-[calc(1rem+var(--safe-bottom))] pt-4 sm:flex-row sm:justify-end">
              {footer}
            </footer>
          ) : (
            <div aria-hidden className="h-[var(--safe-bottom)] shrink-0" />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
